import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * WCAG 4.1.3 - annonce discrete du changement de vue en SPA (lecteurs d'ecran).
 * Le titre de page est mis a jour par les ecrans via useEffect / usePageTitle ; on
 * relit document.title apres un court delai pour rester aligne.
 *
 * WCAG 2.4.3 - deplace le focus vers #main-content apres navigation pour que les
 * utilisateurs clavier ne restent pas bloques sur le menu.
 */
export function SpaRouteAnnouncer() {
  const { pathname } = useLocation();
  const [message, setMessage] = useState('');
  const prevPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevPathRef.current === null) {
      prevPathRef.current = pathname;
      return;
    }
    if (prevPathRef.current === pathname) return;
    prevPathRef.current = pathname;

    const announce = () => {
      const title = typeof document !== 'undefined' ? document.title.trim() : '';
      setMessage(title || pathname);
    };

    // Move focus to main content on route change (WCAG 2.4.3)
    const mainEl = document.getElementById('main-content');
    if (mainEl) {
      mainEl.tabIndex = -1;
      mainEl.focus({ preventScroll: true });
    }

    let innerRaf = 0;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(announce);
    });
    const timeoutId = window.setTimeout(announce, 150);

    return () => {
      cancelAnimationFrame(outerRaf);
      cancelAnimationFrame(innerRaf);
      window.clearTimeout(timeoutId);
    };
  }, [pathname]);

  return (
    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
      {message}
    </div>
  );
}
